/// <reference types="google.maps" />
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command';
import { Input } from '@shared/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { cn } from '@shared/lib/utils';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';

// Utility function to format place name to show only first and last segments
const formatPlaceDisplayName = (formattedAddress: string): string => {
  const segments = formattedAddress.split(',').map((segment) => segment.trim());

  if (segments.length <= 1) {
    return formattedAddress;
  }

  if (segments.length === 2) {
    return formattedAddress;
  }

  // Return first and last segments
  return `${segments[0]}, ${segments[segments.length - 1]}`;
};

interface GooglePlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  defaultValue?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export const GooglePlaceAutocomplete = ({
  onPlaceSelect,
  defaultValue,
  disabled = false,
  className,
  placeholder,
}: GooglePlaceAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(defaultValue || '');
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [placesService, setPlacesService] =
    useState<google.maps.places.PlacesService | null>(null);
  const [autocompleteService, setAutocompleteService] =
    useState<google.maps.places.AutocompleteService | null>(null);

  const places = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize services
  useEffect(() => {
    if (!places) return;

    // Create a dummy div for PlacesService (it needs a map or div element)
    const div = document.createElement('div');
    setPlacesService(new places.PlacesService(div));
    setAutocompleteService(new places.AutocompleteService());
  }, [places]);

  // Handle input changes and get predictions
  useEffect(() => {
    if (!autocompleteService || !inputValue.trim() || inputValue.length < 2) {
      setPredictions([]);
      return;
    }

    const request = {
      input: inputValue,
      types: ['(regions)'],
      fields: ['geometry', 'name', 'formatted_address'],
    };

    autocompleteService.getPlacePredictions(
      request,
      (
        predictions: google.maps.places.AutocompletePrediction[] | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          predictions
        ) {
          setPredictions(predictions);
        } else {
          setPredictions([]);
        }
      }
    );
  }, [autocompleteService, inputValue]);

  const handleSelectPlace = (placeId: string, description: string) => {
    if (!placesService) return;

    const request = {
      placeId: placeId,
      fields: ['geometry', 'name', 'formatted_address'],
    };

    placesService.getDetails(
      request,
      (
        place: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          const displayName = place.formatted_address
            ? formatPlaceDisplayName(place.formatted_address)
            : description;
          setInputValue(displayName);
          onPlaceSelect(place);
          setOpen(false);
        }
      }
    );
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (value.trim() && value.length >= 2) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleInputFocus = () => {
    // Open dropdown if there's valid input
    if (inputValue.trim() && inputValue.length >= 2) {
      setOpen(true);
    }
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    // Check if the blur is moving focus to the popover content
    const relatedTarget = e.relatedTarget as HTMLElement;
    const popoverContent = document.querySelector(
      '[data-radix-popover-content]'
    );

    if (relatedTarget && popoverContent?.contains(relatedTarget)) {
      // Don't close if focus is moving to popover content
      return;
    }

    // Close the dropdown when focus leaves the component
    setOpen(false);
  };

  const handlePopoverOpenChange = (newOpen: boolean) => {
    // Don't let Popover control the state automatically
    // We'll handle all state changes manually
    return;
  };

  return (
    <Popover open={open} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Icons.mapPin className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={cn('pl-9', className)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onClick={() => {
              // Ensure dropdown opens on click, even if input already has focus
              if (inputValue.trim() && inputValue.length >= 2) {
                setOpen(true);
              }
            }}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        side="bottom"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <Command>
          <CommandList>
            {predictions.length === 0 ? (
              <CommandEmpty>
                {inputValue.length < 2
                  ? 'Type to start searching...'
                  : 'No locations found.'}
              </CommandEmpty>
            ) : (
              <CommandGroup>
                {predictions.map((prediction) => (
                  <CommandItem
                    key={prediction.place_id}
                    value={prediction.description}
                    onSelect={() =>
                      handleSelectPlace(
                        prediction.place_id,
                        prediction.description
                      )
                    }
                    onMouseDown={(e) => {
                      // Prevent the input from losing focus when clicking on items
                      e.preventDefault();
                    }}
                    className="flex cursor-pointer items-center"
                  >
                    <div className="flex flex-col">
                      <Text variant="body" className="text-sm">
                        {prediction.structured_formatting?.main_text}
                      </Text>
                      {prediction.structured_formatting?.secondary_text && (
                        <Text variant="subtle" className="text-xs">
                          {prediction.structured_formatting.secondary_text}
                        </Text>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
